import { animate, createTimeline, utils, stagger, onScroll } from 'animejs';

/**
 * Returns a stagger delay (ms) based on the card's column position within its row.
 * Cards in the same row animate in left-to-right with 150 ms gaps.
 */
function getStaggerDelay(card)
{
    const column = card.closest('.wp-block-custom-gutenberg-column');
    const row = column?.parentElement;
    if (!row) return 0;
    const siblings = Array.from(row.children).filter(c => c.querySelector('.destination-card'));
    return Math.max(0, siblings.indexOf(column)) * 150;
}

/**
 * Run the full entrance timeline for a single card.
 * All timing offsets in the timeline are absolute (relative to t=0),
 * shifted by the stagger delay so sibling cards start at different times.
 */
function animateCardIn(card)
{
    const imgEl = card.querySelector('.card-img img');
    const title = card.querySelector('.title-container');
    const cta = card.querySelector('.cta-btn');
    const d = getStaggerDelay(card); // stagger base delay

    createTimeline({ ease: 'outExpo' })

        // 1. Card wrapper — fade in + slide up
        .add(card, {
            opacity: [0, 1],
            translateY: [60, 0],
            duration: 800,
            delay: d,
        })

        // 2. Background image — subtle zoom-out reveal (starts with card)
        .add(imgEl, {
            scale: [1.1, 1],
            duration: 1000,
            ease: 'outQuart',
        }, d)

        // 3. Title container box — slides up with a slight spring overshoot
        .add(title, {
            opacity: [0, 1],
            translateY: [24, 0],
            duration: 650,
            ease: 'outBack(1.4)',
        }, d + 220)

        // 4. CTA button — fades up last
        .add(cta, {
            opacity: [0, 1],
            translateY: [16, 0],
            duration: 480,
        }, d + 450);
}

/**
 * Set the initial hidden state of all card elements, then wire up
 * an IntersectionObserver so cards animate the moment they enter the viewport.
 */
function initEntranceAnimations()
{
    const cards = document.querySelectorAll('.destination-card');
    if (cards.length)
    {
        cards.forEach(card =>
        {
            const imgEl = card.querySelector('.card-img img');
            const title = card.querySelector('.title-container');
            const cta = card.querySelector('.cta-btn');

            utils.set(card, { opacity: 0, translateY: 60 });
            if (imgEl) utils.set(imgEl, { scale: 1.1 });
            if (title) utils.set(title, { opacity: 0, translateY: 24 });
            if (cta) utils.set(cta, { opacity: 0, translateY: 16 });
        });

        const observer = new IntersectionObserver(entries =>
        {
            entries.forEach(entry =>
            {
                if (!entry.isIntersecting) return;
                observer.unobserve(entry.target);
                animateCardIn(entry.target);
            });
        }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

        cards.forEach(card => observer.observe(card));
    }


    animate('.main-object-title', {
        opacity : [0, 1],
        y       : [28, 0],
        duration: 400,
        delay   : 200,
        ease    : 'outQuart',
    });

    animate('.main-object-meta > [class^="col"]', {
        opacity: [0, 1],
        y: [20, 0],
        duration: 600,
        delay: stagger(200, { start: 150 }),
        ease: 'outCubic',
    });

    // ── 2. .icon-circle — subtle scale-in ──────────────────────────
    animate('.icon-circle', {
        scale: [0.6, 1],
        opacity: [0, 1],
        duration: 450,
        delay: stagger(80, { start: 250 }),
        ease: 'outBack(1.4)',
    });

    // Staggered slide-up for each form field + submit button
    animate('.search-travel .form-field, .search-travel .submit-btn', {
        opacity : [0, 1],
        y       : [16, 0],
        duration: 500,
        delay   : stagger(80, { start: 200 }),
        ease    : 'outCubic',
        onComplete(anim) {
            // Remove the inline transform so animated elements no longer create
            // a stacking context — otherwise dropdowns inside them appear behind
            // other content.
            anim.targets.forEach(el => {
                el.style.transform = '';
                el.style.willChange = '';
            });
        },
    });


    // Submit button: subtle scale pulse on hover
   
   


    // ── 4. .show-more-content — fade in the program text ──────────
    // animate('.show-more-content', {
    //     opacity: [0, 1],
    //     y: [12, 0],
    //     duration: 700,
    //     delay: 400,
    //     ease: 'outQuart',
    // });

    // ── 5. h2.h1.text-primary ("Program putovanja") — slide-in ────
    // animate('h2.h1.text-primary', {
    //     opacity: [0, 1],
    //     x: [-20, 0],
    //     duration: 550,
    //     delay: 200,
    //     ease: 'outCubic',
    // });

    // ── 6. #booking — reveal on scroll into viewport ───────────────
    // Uses anime v4 onScroll (IntersectionObserver wrapper)
    animate('#booking h2', {
        opacity: [0, 1],
        y: [18, 0],
        duration: 600,
        delay: stagger(120),
        ease: 'outQuart',
        autoplay: onScroll({ enter: 'bottom 90%' }),
    });

    // animate('#booking [class*="col-lg-3"]', {
    //     opacity: [0, 1],
    //     y: [24, 0],
    //     duration: 500,
    //     delay: stagger(80, { start: 200 }),
    //     ease: 'outCubic',
    //     autoplay: onScroll({ enter: 'bottom 90%' }),
    // });

    // // ── 7. #myTab .nav-link — stagger in tabs on scroll ───────────
    // animate('#myTab .nav-link', {
    //     opacity: [0, 1],
    //     y: [10, 0],
    //     duration: 380,
    //     delay: stagger(70),
    //     ease: 'outQuad',
    //     autoplay: onScroll({ enter: 'bottom 92%' }),
    // });

    // // ── 8. #video-section iframe — fade-in on scroll ───────────────
    // animate('#video-section iframe', {
    //     opacity: [0, 1],
    //     scale: [0.97, 1],
    //     duration: 700,
    //     ease: 'outQuart',
    //     autoplay: onScroll({ enter: 'bottom 90%' }),
    // });

    // // ── 9. .route_route anchors — continuous subtle bob ───────────
    // // The <i class="la-anchor"> icons inside the route row
    // const anchors = document.querySelectorAll('.route_route .la-anchor');
    // anchors.forEach((el, i) => {
    //     animate(el, {
    //         y: [0, -4],
    //         duration: 1800 + i * 120,
    //         alternate: true,
    //         loop: true,
    //         ease: 'inOutSine',
    //     });
    // });

}

/**
 * Hover animations — title box and CTA lift on mouseenter,
 * settling back on mouseleave. Complements the existing CSS image-zoom.
 */
function initHoverAnimations()
{
    document.querySelectorAll('.destination-card').forEach(card =>
    {
        const title = card.querySelector('.title-container');
        const cta = card.querySelector('.cta-btn');

        card.addEventListener('mouseenter', () =>
        {
            if (title) animate(title, { translateY: -8, duration: 380, ease: 'outQuart' });
            if (cta) animate(cta, { translateY: -6, duration: 300, ease: 'outQuart' });
        });

        card.addEventListener('mouseleave', () =>
        {
            if (title) animate(title, { translateY: 0, duration: 420, ease: 'outQuart' });
            if (cta) animate(cta, { translateY: 0, duration: 420, ease: 'outQuart' });
        });
    });
}

document.addEventListener('DOMContentLoaded', () =>
{
    initEntranceAnimations();
    // initHoverAnimations();
});
